import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Loader2, CreditCard, Building2, Check, ArrowUpCircle, Users, FileText } from 'lucide-react';
import { usersAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';

const PLAN_STYLES = {
  free: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Free' },
  starter: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Starter' },
  growth: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Growth' },
  pro: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Pro' },
};

export default function AdminPlans() {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const t = (es, en) => language === 'es' ? es : en;

  const loadOrgs = useCallback(async () => {
    try {
      const data = await usersAPI.getOrganizationPlans();
      setOrgs(data);
    } catch (e) {
      console.error('Error loading orgs:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  const handlePlanChange = async (userId, newPlan) => {
    try {
      setUpdating(userId);
      await usersAPI.updatePlan(userId, newPlan);
      toast({
        title: t('Plan actualizado', 'Plan updated'),
        description: t(`El plan fue cambiado a ${PLAN_STYLES[newPlan]?.label || newPlan}`, `Plan changed to ${PLAN_STYLES[newPlan]?.label || newPlan}`)
      });
      await loadOrgs();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setUpdating(null);
    }
  };

  const stats = {
    total: orgs.length,
    free: orgs.filter(o => (o.subscription_plan || 'free') === 'free').length,
    starter: orgs.filter(o => o.subscription_plan === 'starter').length,
    growth: orgs.filter(o => o.subscription_plan === 'growth').length,
    pro: orgs.filter(o => o.subscription_plan === 'pro').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-plans-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900" data-testid="plans-title">
          {t('Gestion de Planes', 'Plan Management')}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {t('Controla los planes de las organizaciones', 'Control organization plans')}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: t('Total', 'Total'), value: stats.total, icon: Building2, color: 'text-gray-600' },
          { label: 'Free', value: stats.free, icon: Users, color: 'text-gray-500' },
          { label: 'Starter', value: stats.starter, icon: CreditCard, color: 'text-purple-600' },
          { label: 'Growth', value: stats.growth, icon: ArrowUpCircle, color: 'text-blue-600' },
          { label: 'Pro', value: stats.pro, icon: FileText, color: 'text-amber-600' },
        ].map((s, i) => (
          <Card key={i} className="border border-gray-200">
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className={`w-5 h-5 ${s.color}`} />
              <div>
                <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Organizations Table */}
      <Card className="border border-gray-200">
        <CardHeader>
          <CardTitle>{t('Organizaciones', 'Organizations')}</CardTitle>
          <CardDescription>{t('Cambia el plan de cualquier organizacion sin pasar por pasarela de pago', 'Change any organization plan without payment gateway')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('Organizacion', 'Organization')}</TableHead>
                  <TableHead>{t('Contacto', 'Contact')}</TableHead>
                  <TableHead>{t('Plan Actual', 'Current Plan')}</TableHead>
                  <TableHead>{t('Desafios', 'Challenges')}</TableHead>
                  <TableHead>{t('Postulaciones', 'Applications')}</TableHead>
                  <TableHead>{t('Cambiar Plan', 'Change Plan')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orgs.map((org) => {
                  const plan = org.subscription_plan || 'free';
                  const style = PLAN_STYLES[plan] || PLAN_STYLES.free;
                  const isUpdating = updating === org._id;
                  return (
                    <TableRow key={org._id} data-testid={`org-row-${org._id}`}>
                      <TableCell>
                        <div>
                          <p className="font-semibold text-gray-900">{org.organization || org.name}</p>
                          <p className="text-xs text-gray-500">{org.name}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-gray-700">{org.email}</p>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${style.bg} ${style.text} border-0`} data-testid={`plan-badge-${org._id}`}>
                          {style.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-medium text-gray-700">{org.challenges}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-medium text-gray-700">{org.applications}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {isUpdating ? (
                            <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                          ) : (
                            <Select value={plan} onValueChange={(val) => handlePlanChange(org._id, val)}>
                              <SelectTrigger className="w-32 h-8 text-sm" data-testid={`plan-select-${org._id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="free">Free</SelectItem>
                                <SelectItem value="starter">Starter</SelectItem>
                                <SelectItem value="growth">Growth</SelectItem>
                                <SelectItem value="pro">Pro</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                          {plan !== 'free' && (
                            <Badge variant="outline" className="border-green-300 text-green-700 text-xs">
                              <Check className="w-3 h-3 mr-1" /> {t('Activo', 'Active')}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
